import { api } from "./index";

/**
 * Table-icon library for CM-table styling.
 *
 * One route, three verbs, all gated on `manage_configuration`:
 *   POST   v2/admin/table-icons/                      upload
 *   GET    v2/admin/table-icons/[?organisation=<id>]  shared + own
 *   DELETE v2/admin/table-icons/?path=<key>
 *
 * Note the deliberate split in how the org is passed, matching the rest of the
 * admin API: **`organisation_id` in the write body, `organisation` in the read
 * query string**. Only superusers send it at all — an org admin is locked to
 * their own org server-side and the field is ignored.
 */

export interface TableIcon {
  /** CDN-relative key — this is what gets stored in the style's `Icon.image`. */
  path: string;
  /** Absolute CDN URL. Preview only; never persist it (it is environment-specific). */
  url: string;
  /** Original upload name, sanitised. Shown to the admin instead of the UUID path. */
  filename: string;
  size: number;
  /** True for the read-only `public-icons/imw/` library shared by every org. */
  shared: boolean;
  content_type?: string;
  last_modified?: string;
  /** Upload only: the exact bytes already existed, so this is the pre-existing entry. */
  deduplicated?: boolean;
}

export interface TableIconList {
  organisation_id: number | null;
  count: number;
  results: TableIcon[];
}

/** Extensions the API accepts. Mirrored client-side purely to fail faster. */
export const ALLOWED_ICON_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "svg"] as const;

/** Server-enforced cap. Mirrored client-side purely to fail faster. */
export const MAX_ICON_BYTES = 1024 * 1024;

/**
 * Reason `file` would be rejected, or null when it looks acceptable.
 *
 * The server re-checks all of this (and more — real image bytes, format matching
 * the extension, SVG safety), so this exists only to save a round trip and give
 * an instant message.
 */
export const validateIconFile = (file: File): string | null => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!(ALLOWED_ICON_EXTENSIONS as readonly string[]).includes(ext)) {
    return `Unsupported file type ".${ext}". Use PNG, JPG, WEBP or SVG.`;
  }
  if (file.size > MAX_ICON_BYTES) {
    return `That file is ${Math.round(file.size / 1024)} KB. The limit is 1 MB.`;
  }
  return null;
};

/**
 * Whether an SVG's markup declares intrinsic `width` and `height` on the root.
 *
 * The map loads icons through `createImageBitmap`, which needs intrinsic
 * dimensions — without them the icon silently becomes a transparent pixel. The
 * API rejects these too; catching it here explains why before the upload.
 */
export const svgHasIntrinsicSize = (markup: string): boolean => {
  const root = markup.match(/<svg\b[^>]*>/i)?.[0];
  if (!root) return false;
  return /\bwidth\s*=/i.test(root) && /\bheight\s*=/i.test(root);
};

export const uploadTableIcon = async (file: File, organisationId?: number | null): Promise<TableIcon> => {
  const body = new FormData();
  body.append("file", file);
  if (organisationId != null) body.append("organisation_id", String(organisationId));
  // Let the browser set the multipart boundary — naming Content-Type here breaks it.
  const { data } = await api.post("v2/admin/table-icons/", body);
  return data as TableIcon;
};

export const listTableIcons = async (organisationId?: number | null): Promise<TableIconList> => {
  const { data } = await api.get("v2/admin/table-icons/", {
    params: organisationId != null ? { organisation: organisationId } : {}
  });
  return data as TableIconList;
};

export const deleteTableIcon = async (path: string, organisationId?: number | null): Promise<{ deleted: string }> => {
  const { data } = await api.delete("v2/admin/table-icons/", {
    params: { path, ...(organisationId != null ? { organisation: organisationId } : {}) }
  });
  return data as { deleted: string };
};

/** Tables/datasets still referencing an icon, from a 409 on delete. */
export interface IconReferences {
  tables?: string[];
  spatial_datasets?: number[];
}

/**
 * Message for a failed icon request. The API returns `{ error, message }` and
 * says `message` is safe to show verbatim, so prefer it over anything generic.
 */
export const iconErrorMessage = (e: any, fallback = "Action failed"): string =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

/** `referenced_by` from a 409, or null when this is not a reference conflict. */
export const iconConflictReferences = (e: any): IconReferences | null =>
  e?.response?.status === 409 ? (e?.response?.data?.referenced_by ?? {}) : null;
